import i18n from "../localizations";
import { useRootStore } from "../stores/root";

const DIVISION_KEYS = Object.freeze({
    "FINANCE_DIVISION": "finance_division", 
    "PURCHASE_DIVISION": "purchase_division", 
    "PRODUCTION_DIVISION": "production_division", 
    "HR_DIVISION": "hr_division", 
});

const GAME_STATE = Object.freeze({
    CREATED: 'created',
    STARTED: 'started',
    CANCELED: 'canceled',
    FINISHED: 'finished'
});

const utils = {
    // CONSTANTS
    ALERT_TIME: 180,
    
    DEFAULT_LANGUAGE: "SK",
    LANGUAGES: Object.freeze([
        { code: "SK" },
        { code: "EN" }
    ]),
    
    // Timeouts
    TIMEOUT: 5000,

    // Error Codes
    BAD_REQUEST_ERROR: 400,
    VALIDATION_ERROR: 422,

    PUSHER_STATE: Object.freeze({
        INITIALIZED: "initialized",        
        CONNECTING: "connecting",        
        CONNECTED: "connected",        
        UNAVAILABLE: "unavailable",        
        FAILED: "failed",        
        DISCONNECTED: "disconnected"        
    }),
    
    GAME_STATE: Object.freeze(GAME_STATE),
    
    COLOR_BY_GAME_STATE: Object.freeze({
        [GAME_STATE.CREATED]: 'primary',
        [GAME_STATE.STARTED]: 'success',
        [GAME_STATE.CANCELED]: 'danger',
        [GAME_STATE.FINISHED]: 'warning'
    }),
    
    DIVISION_KEYS: Object.freeze(DIVISION_KEYS),
    
    GENERAL_INFO_COMPONENT_BY_DIVISION: Object.freeze({
        [DIVISION_KEYS.FINANCE_DIVISION]: 'FinanceGeneralInformation',        
        [DIVISION_KEYS.PURCHASE_DIVISION]: 'PurchaseGeneralInformation',        
        [DIVISION_KEYS.PRODUCTION_DIVISION]: 'ProductionGeneralInformation',        
        [DIVISION_KEYS.HR_DIVISION]: 'HRGeneralInformation'     
    }),
    PLAYER_INPUT_COMPONENT_BY_DIVISION: Object.freeze({
        [DIVISION_KEYS.FINANCE_DIVISION]: 'FinanceDivisionInput',        
        [DIVISION_KEYS.PURCHASE_DIVISION]: 'PurchaseDivisionInput',        
        [DIVISION_KEYS.PRODUCTION_DIVISION]: 'ProductionDivisionInput',        
        [DIVISION_KEYS.HR_DIVISION]: 'HRDivisionInput'     
    }),
    OTHER_DIVISION_COMPONENT_BY_DIVISION: Object.freeze({
        [DIVISION_KEYS.PURCHASE_DIVISION]: 'PurchaseOtherDivisionsInput',        
        [DIVISION_KEYS.PRODUCTION_DIVISION]: 'ProductionOtherDivisionsInput',        
        [DIVISION_KEYS.HR_DIVISION]: 'HROtherDivisionsInput'   
    }),

    PREV_ROUND_RESULT_KEYS_BY_DIVISION: Object.freeze({
        [DIVISION_KEYS.FINANCE_DIVISION]: [
            "total_lost_profit", 
            "met_demand_rate"
        ], 
        [DIVISION_KEYS.HR_DIVISION]: [
            "employees_to_produced_cars", 
            "profit_per_employee"
        ],
        [DIVISION_KEYS.PURCHASE_DIVISION]: [
            "met_demand_rate", 
            "total_storage_cost", 
            "produced_cars", 
            "average_cost_per_car"
        ],
        [DIVISION_KEYS.PRODUCTION_DIVISION]: [
            "total_storage_cost",
            "production_efficiency",
            "idle_time",
            "produced_cars",
            "employees_to_produced_cars",
            "average_cost_per_car", 
            "met_production_rate"
        ],
    }),

    /**
     * Check if there are validation errors, if so then it will add them into Pinia store.
     *
     * @param error represents catched error object from axios.
     * @returns {boolean} True if error was handled by function, otherwise will return false.
     */
    resolveValidationErrors(error) {
        const rootState = useRootStore();
        
        if (error.response.status === this.VALIDATION_ERROR) {
            rootState.clearErrors();
            rootState.addErrors(error.response.data.errors);
            window.scrollTo(0, 0);
            return true;
        } else if (error.response.status === this.BAD_REQUEST_ERROR) {
            rootState.setLoading(false);
            rootState.addErrors({ login: [i18n.t("errorMessages.login.invalid_grant")] });
            return true;
        }
    },

    /**
     * Check if an object is empty.
     *
     * @param  object Represents any object.
     * @returns {boolean}
     */
    isObjectEmpty(object) {
        return Object.keys(object).length === 0;
    },

    /**
     * Create a notification object and return it.
     *
     * @param {string}  type Represents type and accepts success, warning and danger.
     * @param {string} message Represents text in the notificaiton.
     * @returns {{text: string, type: string}}
     */
    createNotification(type, message) {
        return {
            type: type,
            text: message
        }
    },

    isInDivisions(division, divisionsNames) {
        return divisionsNames.includes(division.key);
    },

    findDivisionOfPlayer(user, divisions) {
        let found = null;

        divisions.some(division => {
            if (division['user_id'] === user.id) {
                found = division;
                return true;
            }

            return false;
        });

        return found;
    },

    formatNumber(number, decimals=2,decimalPoint=',', separator=' ') {
        let result = '';

        const splitNumber = number.toString().split('.');
        const integerPart = splitNumber[0].split('').reverse().join('');
        const decimalPart = splitNumber.length > 1 ? splitNumber[1] : 0;

        for (let i = 1; i < integerPart.length + 1; i++) {
            result = integerPart[i - 1] + result;

            if ((i / 3) > 0 && (i % 3) === 0) {
                result = ' ' + result;
            }

        }

        if (decimals > 0) {
            result += decimalPoint + decimalPart.toString().substr(0, decimals);
        }

        return result;
    },

    formatTimeToMMSS(seconds) {
        if (seconds === null || seconds === undefined || isNaN(seconds) || seconds < 0) {
            return "00:00";
        }
        
        let min = Math.floor(seconds / 60);
        let sec = seconds % 60;

        return min.toString().padStart(2, '0') + ':' + sec.toString().padStart(2, '0');
    }
};

export default utils;
